/** Teilen (MP-B): öffentliche Regal-Seite freigeben, Link und QR, Regal-Collage als Bild, Teilen-Menü, Laden als Kontakt, Anfahrt. */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { encode } from "uqr";
import { Button, Notice, Seitenzustand, TextField } from "../ui";
import { Card } from "../ui/Layout";
import { useMieterStart } from "./api";
import { teilen, useCockpit, usePortalSpeichern } from "./cockpit-api";
import { fotoUrl, useMeineArtikel } from "./artikel-api";
import { kartenLink } from "../seiten/RegalSeite";
import { useTx } from "../i18n";

function Qr({ text, size = 180 }: { text: string; size?: number }) {
  const qr = encode(text, { ecc: "M", border: 2 });
  const d = qr.data.map((z, y) => z.map((an, x) => (an ? `M${x} ${y}h1v1h-1z` : "")).join("")).join("");
  return (
    <svg viewBox={`0 0 ${qr.size} ${qr.size}`} width={size} height={size} role="img" aria-label="QR-Code zur Regal-Seite" style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-md)" }}>
      <path d={d} fill="var(--ink)" />
    </svg>
  );
}

/** Collage: bis zu 9 Fotos im 3×3-Raster, Ladenname, Regal, Link – als PNG zum Teilen oder Speichern. */
async function collageErzeugen(fotos: string[], titel: string, untertitel: string, link: string): Promise<Blob> {
  const S = 1080;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S + 200;
  const g = c.getContext("2d")!;
  const stil = getComputedStyle(document.documentElement);
  const t = (name: string) => stil.getPropertyValue(name).trim();
  const violett = t("--violett");
  const limette = t("--limette");
  const weiss = t("--on-violett");
  g.fillStyle = t("--surface-raised");
  g.fillRect(0, 0, c.width, c.height);
  const n = Math.min(9, fotos.length);
  const spalten = n <= 1 ? 1 : n <= 4 ? 2 : 3;
  const zeilen = Math.ceil(n / spalten) || 1;
  const kante = S / spalten;
  const bilder = await Promise.all(fotos.slice(0, 9).map((u) => new Promise<HTMLImageElement | null>((ok) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => ok(i); i.onerror = () => ok(null); i.src = u; })));
  bilder.forEach((img, i) => {
    const x = (i % spalten) * kante;
    const y = Math.floor(i / spalten) * (S / zeilen);
    const h = S / zeilen;
    if (!img) {
      g.fillStyle = t("--surface-sunken");
      g.fillRect(x + 4, y + 4, kante - 8, h - 8);
      return;
    }
    const f = Math.max(kante / img.width, h / img.height);
    const w = img.width * f, hh = img.height * f;
    g.save();
    g.beginPath();
    g.rect(x + 4, y + 4, kante - 8, h - 8);
    g.clip();
    g.drawImage(img, x + (kante - w) / 2, y + (h - hh) / 2, w, hh);
    g.restore();
  });
  g.fillStyle = violett;
  g.fillRect(0, S, c.width, 200);
  g.fillStyle = limette;
  g.fillRect(0, S, 16, 200);
  g.fillStyle = weiss;
  g.font = "bold 56px Inter, system-ui, sans-serif";
  g.fillText(titel, 48, S + 84);
  g.font = "32px Inter, system-ui, sans-serif";
  g.fillText(untertitel, 48, S + 134);
  g.font = "28px Inter, system-ui, sans-serif";
  g.fillStyle = limette;
  g.fillText(link.replace(/^https?:\/\//, ""), 48, S + 176);
  return new Promise((ok, nein) => c.toBlob((b) => (b ? ok(b) : nein(new Error("Bild konnte nicht erzeugt werden."))), "image/png"));
}

export function Teilen() {
  const tx = useTx();
  const start = useMieterStart();
  const ck = useCockpit();
  const portal = usePortalSpeichern();
  const artikel = useMeineArtikel("im_regal");
  const [name, setName] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const e = ck.data?.einstellungen;
  const d = start.data;
  const link = useMemo(() => (d ? `${location.origin}/r/${d.laden.slug}/${d.mieter.mieternummer}` : ""), [d]);
  const fotos = (artikel.data?.artikel ?? []).filter((a) => a.fotoKey).map((a) => fotoUrl(a.fotoKey)!);

  async function collageTeilen(nurSpeichern = false) {
    if (!d) return;
    setLaeuft(true);
    setMeldung(null);
    try {
      const blob = await collageErzeugen(fotos, `${e?.anzeigename || d.mieter.name.split(" ")[0]} bei ${d.laden.name}`, `Regal ${d.buchungen.map((b) => b.regalNummer).join(", ")} · jetzt im Laden`, link);
      const datei = new File([blob], "mein-regal.png", { type: "image/png" });
      if (!nurSpeichern && navigator.canShare?.({ files: [datei] })) {
        await navigator.share({ files: [datei], title: `Mein Regal bei ${d.laden.name}`, text: `Schau mal: ${link}` });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "mein-regal.png";
        a.click();
        setMeldung("Bild gespeichert – jetzt in Instagram oder WhatsApp posten.");
      }
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : "Das hat nicht geklappt.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Seitenzustand query={start} lines={5}>
      {(d) => (
        <div className="sc-col" style={{ gap: "var(--space-4)" }}>
          <div className="sc-row">
            <Link to="/mieter" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
            <h1 className="title">{tx("Teilen")}</h1>
          </div>
          {meldung ? <Notice tone="info">{meldung}</Notice> : null}
          <Card title={tx("Deine Regal-Seite")}>
            <p className="sc-muted">{tx("Eine öffentliche Seite mit deinen Artikeln, Fotos und Preisen – ohne Namen, Adresse oder Guthaben. Zum Teilen auf Instagram, WhatsApp oder als QR am Regal.")}</p>
            {e ? (
              <>
                <label className="sc-check" style={{ margin: "var(--space-3) 0" }}>
                  <input type="checkbox" checked={e.oeffentlich} onChange={(ev) => portal.mutate({ ...e, oeffentlich: ev.target.checked })} /> Seite freigeben
                </label>
                <TextField label="Anzeigename" value={name ?? e.anzeigename} onChange={(ev) => setName(ev.target.value)} placeholder={d.mieter.name.split(" ")[0]} maxLength={40} hint="Vorname oder Fantasiename – mehr sieht niemand" />
                {name !== null && name !== e.anzeigename ? <Button variant="secondary" icon="check" loading={portal.isPending} onClick={() => portal.mutate({ ...e, anzeigename: name.trim() }, { onSuccess: () => setName(null) })}>Namen speichern</Button> : null}
                {e.oeffentlich ? (
                  <div className="sc-col sc-center" style={{ gap: "var(--space-3)", marginTop: "var(--space-4)" }}>
                    <Qr text={link} />
                    <a href={link} target="_blank" rel="noreferrer" className="app-code" style={{ wordBreak: "break-all" }}>{link.replace(/^https?:\/\//, "")}</a>
                    <div className="sc-toolbar">
                      <Button variant="primary" icon="sparkles" onClick={() => void teilen({ titel: `Mein Regal bei ${d.laden.name}`, text: `Schau mal, was ich bei ${d.laden.name} verkaufe:`, url: link }).then((r) => setMeldung(r === "kopiert" ? "Link kopiert." : r === "nein" ? "Teilen hat nicht geklappt." : null))}>Link teilen</Button>
                      <Button variant="secondary" icon="copy" onClick={() => void navigator.clipboard.writeText(link).then(() => setMeldung("Link kopiert."))}>Kopieren</Button>
                    </div>
                    <p className="sc-muted sc-small">QR-Code ausdrucken und ans Regal kleben – Kunden sehen alle deine Artikel mit Fotos.</p>
                  </div>
                ) : null}
              </>
            ) : null}
          </Card>
          <Card title={tx("Regal-Collage")}>
            <p className="sc-muted">{tx("Ein Bild aus bis zu neun Artikelfotos mit Ladenname und Link – fertig für Instagram oder WhatsApp.")}</p>
            {fotos.length === 0 ? <p className="sc-small">Erst Artikel mit Foto erfassen, dann gibt es hier die Collage.</p> : null}
            <div className="sc-toolbar">
              <Button variant="primary" icon="camera" loading={laeuft} disabled={fotos.length === 0} onClick={() => void collageTeilen(false)}>Collage teilen</Button>
              <Button variant="secondary" loading={laeuft} disabled={fotos.length === 0} onClick={() => void collageTeilen(true)}>Als Bild speichern</Button>
            </div>
          </Card>
          <Card title={d.laden.name}>
            <div className="sc-toolbar">
              <a href={`/api/public/laden/${d.laden.slug}/kontakt.vcf`} className="rg-btn rg-btn--secondary">Laden als Kontakt speichern</a>
              {ladenAdresse(d) ? <a href={kartenLink(ladenAdresse(d)!, d.laden.name)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary">Anfahrt</a> : null}
            </div>
          </Card>
        </div>
      )}
    </Seitenzustand>
  );
}

function ladenAdresse(d: { laden: { adresse?: { strasse?: string; plz?: string; ort?: string } } }) {
  return d.laden.adresse ?? null;
}
